import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import BobineLogo from '../components/BobineLogo';
import AlbumBook from '../components/AlbumBook';
import { usePhotoSwipe } from '../hooks/usePhotoSwipe';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;

const SharedGalleryPage = () => {
  const { shareToken } = useParams();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [bookOpened, setBookOpened] = useState(false);
  const [cocoonOpening, setCocoonOpening] = useState(false);

  // Pluie d'Or — Web Audio API pour BOOST de volume au-delà de 1.0
  // (HTMLAudio plafonne à 1.0, le mp3 est trop discret au touch de l'écrin)
  const audioCtxRef = useRef(null);
  const audioBufferRef = useRef(null);
  const pluieOrRef = useRef(null); // fallback HTMLAudio (iOS strict)
  useEffect(() => {
    if (!data?.album_name) return;
    // Fallback HTMLAudio toujours préchargé (iOS exige une lecture issue d'un user gesture sur HTMLAudio)
    const audio = new Audio('/bobine-pluie-or.mp3');
    audio.preload = 'auto';
    audio.volume = 1.0;
    pluieOrRef.current = audio;
    try { audio.load(); } catch {}
    // Web Audio decode (boost gain) — best effort, ignoré si non supporté
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      fetch('/bobine-pluie-or.mp3')
        .then(r => r.arrayBuffer())
        .then(buf => ctx.decodeAudioData(buf))
        .then(decoded => { audioBufferRef.current = decoded; })
        .catch(() => {});
    } catch {}
  }, [data?.album_name]);

  const handleOpenCocoon = useCallback(() => {
    // Évite double-déclenchement pendant l'animation magique
    if (cocoonOpening || bookOpened) return;
    // 1) Web Audio (boost x2.8) — le plus puissant
    const ctx = audioCtxRef.current;
    const buf = audioBufferRef.current;
    let webAudioOK = false;
    try {
      if (ctx && buf) {
        if (ctx.state === 'suspended' && ctx.resume) ctx.resume();
        const src = ctx.createBufferSource();
        src.buffer = buf;
        const gain = ctx.createGain();
        // Phase d'OUVERTURE (0 → 1.8s) : gain plein 2.8 (magie pleine puissance)
        // Phase GRILLE (>1.8s) : fade vers 1.0 (présence douce, pas écrasant)
        const now = ctx.currentTime;
        gain.gain.setValueAtTime(2.8, now);
        gain.gain.setValueAtTime(2.8, now + 1.8);
        gain.gain.linearRampToValueAtTime(1.0, now + 2.6);
        src.connect(gain).connect(ctx.destination);
        src.start(0);
        webAudioOK = true;
      }
    } catch {}
    // 2) Fallback HTMLAudio en parallèle (iOS Safari)
    if (!webAudioOK) {
      try {
        const a = pluieOrRef.current;
        if (a) {
          a.currentTime = 0;
          a.volume = 1.0;
          const p = a.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }
      } catch {}
    }
    // 3) Animation magique d'ouverture — 1.8s avant la grille
    setCocoonOpening(true);
    setTimeout(() => {
      setBookOpened(true);
      setCocoonOpening(false);
    }, 1800);
  }, [cocoonOpening, bookOpened]);

  const goPrev = useCallback(() => { if (viewerIndex > 0) setViewerIndex(viewerIndex - 1); }, [viewerIndex]);
  const goNext = useCallback(() => { if (data && viewerIndex < data.photos.length - 1) setViewerIndex(viewerIndex + 1); }, [viewerIndex, data]);

  // Préchargement des photos adjacentes (suivante + précédente) pour éviter
  // les 3 secondes d'attente entre 2 photos. Appelé dès que viewerIndex change.
  useEffect(() => {
    if (viewerIndex === null || !data?.photos) return;
    const apiBase = (process.env.REACT_APP_BACKEND_URL || 'https://bobine.live');
    const preload = (idx) => {
      const p = data.photos[idx];
      if (!p) return;
      const img = new Image();
      img.src = `${apiBase}${p.image_url}`;
    };
    preload(viewerIndex + 1);
    preload(viewerIndex + 2);
    preload(viewerIndex - 1);
  }, [viewerIndex, data]);

  // Robust swipe + tap via @use-gesture/react
  const viewerSwipe = usePhotoSwipe({
    isOpen: viewerIndex !== null,
    onClose: useCallback(() => setViewerIndex(null), []),
    onPrev: goPrev,
    onNext: goNext,
    totalPhotos: data?.photos?.length || 0,
    currentIndex: viewerIndex ?? 0,
  });

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const res = await fetch(`${API}/sharing/${shareToken}`);
        if (!res.ok) {
          if (res.status === 404) {
            setError("Ce lien de partage est invalide ou a expiré.");
          } else {
            setError("Erreur lors du chargement.");
          }
          setLoading(false);
          return;
        }
        const json = await res.json();
        setData(json);
      } catch {
        setError("Impossible de charger la galerie.");
      }
      setLoading(false);
    };
    fetchShared();
  }, [shareToken]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          {/* Bobine dorée qui tourne — logo réel rotatif (pas un cercle vide) */}
          <img
            src="/bobine-reel-transparent.png"
            alt="Bobine"
            style={{
              width: 64, height: 64, objectFit: 'contain',
              filter: 'drop-shadow(0 0 14px rgba(212,175,55,0.55))',
              animation: 'bobineSpin 2.4s linear infinite',
            }}
          />
          <p style={{ fontFamily: "'Georgia', serif", color: '#D4AF37', fontSize: 13, marginTop: 22, letterSpacing: 2, fontStyle: 'italic', opacity: 0.75 }}>
            le fil se déroule…
          </p>
          <style>{`
            @keyframes bobineSpin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: 40 }}>
          <BobineLogo size={28} />
          <p data-testid="share-error" style={{ fontFamily: "'Georgia', serif", color: '#D4AF37', fontSize: 16, marginTop: 24, lineHeight: 1.7 }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000' }}>
      {/* ═══════════════════════════════════════════════════
          ÉTAPE 1 — L'ÉCRIN SEUL : visible uniquement pour un partage
          d'écrin (album_name présent) tant que l'utilisateur n'a pas
          cliqué dessus. Plein écran noir, livre centré, pas de grille.
          ═══════════════════════════════════════════════════ */}
      {data.album_name && !bookOpened ? (
        <div data-testid="share-cocoon-step1" style={{
          position: 'fixed', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: 20, backgroundColor: '#000',
          animation: 'fadeIn 0.6s ease-out both',
          overflow: 'hidden',
        }}>
          {/* Halo doré qui grossit pendant l'ouverture magique */}
          {cocoonOpening && (
            <div aria-hidden="true" style={{
              position: 'absolute', inset: 0,
              background: 'radial-gradient(circle at center, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0.12) 35%, transparent 70%)',
              animation: 'cocoonHaloRise 1.8s ease-out forwards',
              pointerEvents: 'none',
            }} />
          )}
          {(() => {
            const coverPhotoId = data.album_cover_photo_id || (data.photos && data.photos[0]?.id);
            const coverSrc = coverPhotoId
              ? `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api/sharing/${shareToken}/photo/${coverPhotoId}/thumbnail`
              : null;
            return (
              <div style={{
                animation: cocoonOpening ? 'cocoonBookRise 1.8s cubic-bezier(0.4, 0.0, 0.2, 1) forwards' : 'none',
                willChange: 'transform, opacity, filter',
                transformOrigin: 'center center',
              }}>
                <AlbumBook
                  album={{
                    id: 'shared-step1',
                    name: data.album_name,
                    cover_color: data.album_cover_color || 'noir',
                    cover_photo_id: data.album_cover_photo_id || null,
                    photo_count: data.photo_count || 0,
                  }}
                  size="xl"
                  isActive={false}
                  coverImageSrc={coverSrc}
                  onClick={() => handleOpenCocoon()}
                />
              </div>
            );
          })()}
          <p data-testid="share-cocoon-hint" style={{
            fontFamily: "'Georgia', serif", fontSize: 13,
            color: '#D4AF37', marginTop: 38,
            letterSpacing: 2.5, textTransform: 'uppercase', fontWeight: 600,
            opacity: cocoonOpening ? 0 : 0.85,
            animation: cocoonOpening ? 'none' : 'cocoonPulse 2.4s ease-in-out infinite',
            transition: 'opacity 0.4s ease',
          }}>
            Touchez l'écrin pour l'ouvrir
          </p>
          <a
            data-testid="share-cocoon-discover"
            href="/"
            style={{
              fontFamily: "'Georgia', serif", fontSize: 12,
              color: '#D4AF37', marginTop: 28,
              fontStyle: 'italic', letterSpacing: 0.6,
              textDecoration: 'none', opacity: 0.5,
              transition: 'opacity 0.3s',
            }}
            onMouseEnter={e => e.target.style.opacity = '0.9'}
            onMouseLeave={e => e.target.style.opacity = '0.5'}
          >
            Découvrir Bobine&nbsp;&nbsp;→
          </a>
          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes cocoonPulse {
              0%, 100% { opacity: 0.5; transform: translateY(0); }
              50% { opacity: 1; transform: translateY(-2px); }
            }
            /* OUVERTURE MAGIQUE — 1.8s, courbe cinématique */
            @keyframes cocoonBookRise {
              0%   { transform: scale(1) translateY(0) rotateX(0deg);  filter: drop-shadow(0 0 0px rgba(212,175,55,0)); opacity: 1; }
              25%  { transform: scale(1.06) translateY(-8px) rotateX(2deg); filter: drop-shadow(0 8px 30px rgba(212,175,55,0.45)); opacity: 1; }
              55%  { transform: scale(1.18) translateY(-26px) rotateX(8deg); filter: drop-shadow(0 14px 50px rgba(212,175,55,0.75)); opacity: 1; }
              80%  { transform: scale(1.32) translateY(-44px) rotateX(14deg); filter: drop-shadow(0 22px 70px rgba(212,175,55,0.95)); opacity: 0.85; }
              100% { transform: scale(1.55) translateY(-70px) rotateX(22deg); filter: drop-shadow(0 30px 90px rgba(212,175,55,1)); opacity: 0; }
            }
            @keyframes cocoonHaloRise {
              0%   { opacity: 0; transform: scale(0.6); }
              35%  { opacity: 0.7; transform: scale(1.1); }
              75%  { opacity: 1; transform: scale(1.6); }
              100% { opacity: 0; transform: scale(2.2); }
            }
          `}</style>

        </div>
      ) : (
      <>
      {/* Croix dorée fermeture — RETOURNE au cocon (l'écrin fermé) si la grille est ouverte,
          ou ferme la visionneuse si elle est ouverte. La page elle-même reste accessible. */}
      <button
        data-testid="fildesoie-close-btn"
        onClick={() => {
          if (viewerIndex !== null) {
            setViewerIndex(null);
          } else {
            setBookOpened(false);
          }
        }}
        title="Refermer l'écrin"
        style={{
          position: 'fixed', top: 12, right: 12, zIndex: 60,
          background: 'rgba(10,10,10,0.7)', border: '1px solid rgba(212,175,55,0.4)',
          borderRadius: '50%', width: 44, height: 44,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.3s',
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>

      {/* Header */}
      <header style={{ borderBottom: '1px solid rgba(212,175,55,0.2)', padding: '16px 0' }}>
        <div className="container mx-auto px-4" style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <BobineLogo size={20} />
          <LanguageSelector />
        </div>
      </header>

      {/* Invitation message */}
      <div style={{ textAlign: 'center', padding: '40px 20px 20px' }}>
        <p data-testid="share-message" style={{
          fontFamily: "'Georgia', serif",
          fontSize: 20,
          color: '#D4AF37',
          fontStyle: 'italic',
          lineHeight: 1.6,
          maxWidth: 500,
          margin: '0 auto',
        }}>
          {data.message}
        </p>
      </div>

      {/* Hero "Livre" — réduit en après-clic : juste l'étiquette + compteur (le livre a déjà joué son rôle d'entrée) */}
      {data.album_name && (
        <div data-testid="share-album-hero" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '12px 20px 24px', textAlign: 'center',
          animation: 'shareRevealIn 0.7s ease-out 0.2s both',
        }}>
          <p data-testid="share-album-name" style={{
            fontFamily: "'Georgia', serif",
            fontSize: 14,
            color: '#D4AF37',
            letterSpacing: 2.5,
            textTransform: 'uppercase',
            fontWeight: 600,
          }}>
            Écrin · {data.album_name}
          </p>
          <p style={{
            fontFamily: "'Georgia', serif",
            fontSize: 13,
            color: '#D4AF37',
            opacity: 0.7,
            marginTop: 6, fontStyle: 'italic',
          }}>
            {data.photo_count} {data.photo_count > 1 ? t('photos') : t('photo')}
          </p>
          <div style={{ width: 60, height: 1, backgroundColor: 'rgba(212,175,55,0.25)', marginTop: 20 }} />
          <style>{`
            @keyframes shareRevealIn {
              from { opacity: 0; transform: translateY(12px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}

      {/* Compteur photos seul (cas partage GLOBAL sans album) */}
      {!data.album_name && (
        <div style={{ textAlign: 'center', padding: '0 20px 20px' }}>
          <p style={{
            fontFamily: "'Georgia', serif",
            fontSize: 13,
            color: '#D4AF37',
            marginTop: 12,
          }}>
            {data.photo_count} {data.photo_count > 1 ? t('photos') : t('photo')}
          </p>
        </div>
      )}

      {/* Gallery grid */}
      <div className="container mx-auto px-4" style={{ paddingBottom: 60 }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 12,
          maxWidth: 900,
          margin: '0 auto',
        }}>
          {data.photos.map((photo, idx) => (
            <div
              key={photo.id}
              data-testid={`shared-photo-${idx}`}
              onClick={() => setViewerIndex(idx)}
              style={{
                aspectRatio: '1',
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid rgba(212,175,55,0.15)',
                cursor: 'pointer',
                transition: 'all 0.3s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#D4AF37'; e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#D4AF37'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <img
                src={`${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}${photo.thumbnail_url}`}
                alt={photo.caption || photo.filename}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Immersive viewer */}
      {viewerIndex !== null && data.photos[viewerIndex] && (
        <div
          className={viewerSwipe.enterDir}
          data-testid="shared-viewer-modal"
          onMouseMove={viewerSwipe.handleMouseMove}
          style={{
            position: 'fixed', inset: 0, zIndex: 50,
            backgroundColor: '#000',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            touchAction: 'none',
            overflow: 'hidden',
            userSelect: 'none',
          }}
        >
          {/* Close X — top right, FIXED for mobile */}
          <button
            data-testid="shared-viewer-close"
            onClick={(e) => { e.stopPropagation(); setViewerIndex(null); }}
            style={{
              position: 'fixed', top: 16, right: 16, zIndex: 55,
              background: 'rgba(10,10,10,0.7)', border: '1px solid rgba(212,175,55,0.4)',
              borderRadius: '50%', width: 44, height: 44,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.3s',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>

          {/* Photo counter — top left */}
          <div style={{
            position: 'absolute', top: 20, left: 20, zIndex: 10,
            fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37',
            letterSpacing: 1,
          }}>
            {viewerIndex + 1} / {data.photos.length}
          </div>

          {/* Tap zones — gauche/droite pour navigation foolproof (au cas où le swipe ne marche pas) */}
          {viewerIndex > 0 && (
            <button
              data-testid="shared-viewer-tap-prev"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              aria-label="Photo précédente"
              style={{
                position: 'fixed', left: 0, top: 0, bottom: 0,
                width: '30vw', minWidth: 80, maxWidth: 200,
                zIndex: 8,
                background: 'transparent', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                paddingLeft: 14,
                WebkitTapHighlightColor: 'rgba(212,175,55,0.15)',
              }}
            >
              <span style={{
                width: 38, height: 38, borderRadius: '50%',
                background: 'rgba(10,10,10,0.55)',
                border: '1px solid rgba(212,175,55,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: 0.7,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              </span>
            </button>
          )}
          {viewerIndex < data.photos.length - 1 && (
            <button
              data-testid="shared-viewer-tap-next"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              aria-label="Photo suivante"
              style={{
                position: 'fixed', right: 0, top: 0, bottom: 0,
                width: '30vw', minWidth: 80, maxWidth: 200,
                zIndex: 8,
                background: 'transparent', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                paddingRight: 14,
                WebkitTapHighlightColor: 'rgba(212,175,55,0.15)',
              }}
            >
              <span style={{
                width: 38, height: 38, borderRadius: '50%',
                background: 'rgba(10,10,10,0.55)',
                border: '1px solid rgba(212,175,55,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: 0.7,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
              </span>
            </button>
          )}

          {/* PC hover arrows — thin golden chevrons */}
          {viewerIndex > 0 && (
            <button
              data-testid="shared-viewer-prev"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                zIndex: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 12,
                opacity: viewerSwipe.showArrows ? 0.7 : 0, transition: 'opacity 0.4s ease',
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
          )}
          {viewerIndex < data.photos.length - 1 && (
            <button
              data-testid="shared-viewer-next"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              style={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                zIndex: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 12,
                opacity: viewerSwipe.showArrows ? 0.7 : 0, transition: 'opacity 0.4s ease',
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          )}

          {/* Photo — wrap dans un container qui capte les pointer events pour le swipe */}
          <div
            ref={viewerSwipe.photoRef}
            onClick={e => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '94vw', maxHeight: '85vh',
              touchAction: 'pan-y',
              userSelect: 'none',
              cursor: 'grab',
            }}
          >
            <img
              src={`${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}${data.photos[viewerIndex].image_url}`}
              alt=""
              draggable={false}
              style={{
                display: 'block',
                maxWidth: '94vw', maxHeight: '85vh', objectFit: 'contain',
                userSelect: 'none', pointerEvents: 'none',
              }}
            />
          </div>

          {/* Caption overlay — bottom of photo */}
          {data.photos[viewerIndex].caption && (
            <div style={{
              position: 'absolute', bottom: showInfo ? 140 : 50, left: '50%', transform: 'translateX(-50%)',
              zIndex: 10, transition: 'bottom 0.3s ease',
            }}>
              <p style={{
                fontFamily: "'Georgia', serif", color: '#D4AF37',
                fontSize: 13, textAlign: 'center', fontStyle: 'italic',
                textShadow: '0 1px 4px rgba(0,0,0,0.8)',
              }}>
                {data.photos[viewerIndex].caption}
              </p>
            </div>
          )}

          {/* Info toggle button — bottom center */}
          {(data.photos[viewerIndex].audio_transcription || data.photos[viewerIndex].audio_url || data.photos[viewerIndex].eclaireur_comments?.length > 0) && (
            <button
              data-testid="shared-viewer-info-toggle"
              onClick={(e) => { e.stopPropagation(); setShowInfo(!showInfo); }}
              style={{
                position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
                zIndex: 10, fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37',
                background: 'rgba(212,175,55,0.1)', border: '1.5px solid rgba(212,175,55,0.5)',
                borderRadius: 24, padding: '8px 22px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: '0 0 16px rgba(212,175,55,0.15)',
                transition: 'all 0.3s', fontWeight: 500,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round">
                {showInfo ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
              </svg>
              {showInfo ? 'Masquer' : 'Histoires'}
            </button>
          )}

          {/* Collapsible info panel */}
          {showInfo && (
            <div
              onClick={e => e.stopPropagation()}
              style={{
                position: 'absolute', bottom: 48, left: '50%', transform: 'translateX(-50%)',
                zIndex: 10, width: '90vw', maxWidth: 440,
                backgroundColor: 'rgba(0,0,0,0.85)',
                border: '1px solid rgba(212,175,55,0.15)',
                borderRadius: 12, padding: '14px 16px',
                maxHeight: '35vh', overflowY: 'auto',
                backdropFilter: 'blur(8px)',
                animation: 'fadeIn 0.2s ease-out',
              }}
            >
              {data.photos[viewerIndex].audio_transcription && (
                <p style={{
                  fontFamily: "'Georgia', serif", color: '#FAF3E0',
                  fontSize: 12, textAlign: 'center', lineHeight: 1.6, fontStyle: 'italic',
                  marginBottom: 10,
                }}>
                  "{data.photos[viewerIndex].audio_transcription}"
                </p>
              )}
              {data.photos[viewerIndex].audio_url && (
                <div data-no-close style={{ textAlign: 'center', marginBottom: 10 }}>
                  <button
                    data-testid="shared-audio-play-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      const btnEl = e.currentTarget;
                      // If already playing, stop
                      if (btnEl._audio && !btnEl._audio.paused) {
                        btnEl._audio.pause();
                        btnEl._audio.currentTime = 0;
                        btnEl._audio = null;
                        btnEl.querySelector('span').textContent = 'Écouter';
                        return;
                      }
                      const a = new Audio(`${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}${data.photos[viewerIndex].audio_url}`);
                      btnEl._audio = a;
                      btnEl.querySelector('span').textContent = 'Arrêter';
                      a.play();
                      a.onended = () => { btnEl._audio = null; btnEl.querySelector('span').textContent = 'Écouter'; };
                    }}
                    style={{
                      fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37',
                      background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.25)',
                      borderRadius: 16, padding: '6px 18px', cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.8" strokeLinecap="round">
                      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
                      <path d="M19 10v2a7 7 0 01-14 0v-2"/>
                      <line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
                    </svg>
                    <span>&#201;couter l'histoire</span>
                  </button>
                </div>
              )}
              {data.photos[viewerIndex].eclaireur_comments?.length > 0 && (
                <div>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 10, color: '#D4AF37', marginBottom: 6, textAlign: 'center', letterSpacing: 1 }}>&#201;CLAIRAGES</p>
                  {data.photos[viewerIndex].eclaireur_comments.map(c => (
                    <div key={c.id} style={{
                      padding: '6px 12px', marginBottom: 4, borderRadius: 6,
                      backgroundColor: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.08)',
                    }}>
                      <span style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37', fontWeight: 600 }}>{c.guest_name}</span>
                      {c.has_audio && (
                        <button onClick={e => {
                            e.stopPropagation();
                            const btn = e.currentTarget;
                            if (btn._audio && !btn._audio.paused) { btn._audio.pause(); btn._audio.currentTime = 0; btn._audio = null; btn.querySelector('span').textContent = 'Écouter'; return; }
                            const a = new Audio(`${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api/eclaireur/audio/${c.id}`);
                            btn._audio = a; btn.querySelector('span').textContent = 'Arrêter';
                            a.play(); a.onended = () => { btn._audio = null; btn.querySelector('span').textContent = 'Écouter'; };
                          }}
                          style={{ marginLeft: 8, fontFamily: "'Georgia', serif", fontSize: 10, color: '#D4AF37', background: '#D4AF37', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 10, padding: '2px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="#D4AF37"><polygon points="5 3 19 12 5 21"/></svg>
                          <span>&#201;couter</span>
                        </button>
                      )}
                      {c.text && c.text !== '(message vocal)' && (
                        <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#FAF3E0', marginTop: 3, fontStyle: 'italic' }}>"{c.text}"</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <footer style={{ borderTop: '1px solid rgba(212,175,55,0.15)', padding: '32px 0 24px', textAlign: 'center' }}>
        <p style={{ fontFamily: "'Georgia', serif", color: '#D4AF37', fontSize: 11, marginBottom: 16 }}>
          Galerie partagée en lecture seule
        </p>
        <a
          href="/"
          data-testid="discover-bobine-link"
          style={{
            fontFamily: "'Georgia', serif",
            fontSize: 12,
            color: '#D4AF37',
            fontStyle: 'italic',
            textDecoration: 'none',
            letterSpacing: 0.5,
            transition: 'color 0.3s',
          }}
          onMouseEnter={e => e.target.style.color = '#D4AF37'}
          onMouseLeave={e => e.target.style.color = '#D4AF37'}
        >
          Créez votre propre coffre-fort de souvenirs sur Bobine&nbsp;→
        </a>
      </footer>
      </>
      )}
    </div>
  );
};

export default SharedGalleryPage;
